import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ChartDisplayContext, keepZoom, presentChart, sameDeps, type ChartAppearance } from './chartPresentation'
import * as echarts from 'echarts/core'
import { BarChart, HeatmapChart, LineChart, ScatterChart } from 'echarts/charts'
import {
  CalendarComponent, DataZoomComponent, GridComponent, LegendComponent,
  MarkAreaComponent, MarkLineComponent, MarkPointComponent, PolarComponent, TooltipComponent, VisualMapComponent,
} from 'echarts/components'
import { LegacyGridContainLabel } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import type { EChartsOption } from 'echarts'

// Registered explicitly rather than importing the whole library, which is
// ~1 MB against 700 kB. Everything the app draws is listed here: a chart type
// that is not registered renders as an empty canvas with no error, so adding
// a new one means adding it here too.
//
// LegacyGridContainLabel keeps `grid.containLabel` meaning what it meant in
// ECharts 5. Without it, ECharts 6 ignores the option (a console warning in
// development, nothing in production) and lays the grid out by its
// `outerBounds` rule instead, which moves every axis by a few pixels and lets
// axis names shift to dodge the tick labels: the time-series and rating-curve
// grids were tuned by hand against the old layout (see their comments).
echarts.use([
  LineChart, ScatterChart, BarChart, HeatmapChart,
  GridComponent, TooltipComponent, LegendComponent, DataZoomComponent,
  PolarComponent, VisualMapComponent, CalendarComponent, MarkPointComponent, MarkAreaComponent, MarkLineComponent,
  LegacyGridContainLabel,
  CanvasRenderer,
])

/** Mount an ECharts instance and keep it in step with `option`.
 *
 *  `notMerge` on every update, deliberately: ECharts merges by default, so
 *  removing a series leaves the old one on screen. On this dashboard that
 *  would mean a chart still showing a station the user has navigated away
 *  from, which is worse than a flicker.
 */
/** Charts are typeset like the rest of the interface, not like a library
 *  demo: FiraGO, Jet Black, and a white plot background. ECharts otherwise
 *  defaults to its own sans-serif at #333, which reads as a different
 *  document sitting inside this one. docs/DESIGN_GUIDELINE.md sections 3 and
 *  7. Applied as a base here rather than repeated in five components, and
 *  merged under the caller's option so a chart can still say more. */
const BASE: EChartsOption = {
  backgroundColor: '#FFFFFF',
  textStyle: {
    fontFamily: '"FiraGO", "Segoe UI", system-ui, sans-serif',
    color: '#000000',
  },
}

/** `deps` name what the chart is of: a change is a new question, and the zoom
 *  starts over. `redraw` are things that change only how it is drawn (a mode, a
 *  pin's band, a measurement): a change draws it again and keeps the zoom, as a
 *  change of line appearance does. */
export function useECharts(option: EChartsOption, deps: unknown[] = [], redraw: unknown[] = []) {
  const display = useContext(ChartDisplayContext)
  const [localAppearance, setLocalAppearance] = useState<ChartAppearance>({})
  const appearance = useMemo(() => display.onChange ? display.value ?? {} : { ...display.value, ...localAppearance }, [display.value, display.onChange, localAppearance])
  const container = useRef<HTMLDivElement>(null) as ChartContainer
  container.presentation = { option, appearance, change: display.onChange ?? setLocalAppearance, visible: display.showControls !== false }
  const chart = useRef<echarts.ECharts | null>(null)
  // The deps of the last drawing: a drawing for the same deps (only the line
  // appearance changed) keeps the reader's zoom, a new question does not.
  const drawnFor = useRef<unknown[] | null>(null)

  useEffect(() => {
    if (!container.current) return
    chart.current = echarts.init(container.current)
    const resize = () => chart.current?.resize()
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('resize', resize)
      chart.current?.dispose()
      chart.current = null
    }
  }, [])

  useEffect(() => {
    let next = presentChart({ ...BASE, ...option }, appearance)
    if (drawnFor.current && sameDeps(drawnFor.current, deps)) {
      next = keepZoom(next, chart.current?.getOption()?.dataZoom as Parameters<typeof keepZoom>[1])
    }
    drawnFor.current = deps
    chart.current?.setOption(next, true)
    // `option` is rebuilt every render by callers; deps is the real list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, ...redraw, appearance])

  return container
}

export type ChartContainer = React.RefObject<HTMLDivElement> & { presentation?: {
  option: EChartsOption; appearance: ChartAppearance; change: (value: ChartAppearance) => void; visible: boolean
} }
